import { z } from 'zod';
import { PipelineKind, StageOutcome } from '../enums.js';
const trim = z.string().trim();
/**
 * Configurazione delle pipeline commerciali (/api/v1/pipelines).
 *
 * Fino a ieri pipeline e step vivevano solo nel seed
 * (`apps/api/prisma/seed-pipelines.ts`): aggiungerne una voleva dire un
 * intervento tecnico. Qui c'è il contratto che permette di farlo dalla
 * schermata, senza toccare il codice.
 *
 * Due cose NON si toccano da fuori, e per un motivo:
 *  - lo `slug`, che il server ricava dal nome alla creazione: i link e la
 *    preferenza salvata nel browser lo usano come chiave, cambiarlo li
 *    romperebbe in silenzio;
 *  - l'ordine, che non si digita: si sposta di un posto per volta
 *    (`direction`), così non si finisce con due step allo stesso numero.
 */
/** Uno step in creazione: il nome lo decide chi configura, l'ordine è la posizione nell'array. */
export const pipelineStageInput = z.object({
    name: trim.min(1).max(80),
    /** Quanto è probabile chiudere da qui: è il default delle trattative che ci arrivano. */
    probability: z.coerce.number().int().min(0).max(100).default(0),
    /** Se lo step vale "in corso", "vinta" o "persa": è l'unica cosa che il codice guarda. */
    outcome: z.nativeEnum(StageOutcome).default(StageOutcome.APERTO),
});
/**
 * Nuova pipeline. Senza `stages` il server mette quattro step di partenza (due
 * in corso, uno vinto, uno perso): una pipeline senza step non è utilizzabile, e
 * lasciare l'utente davanti a una lista vuota è un modo per farlo sbagliare.
 */
export const createPipelineInput = z.object({
    name: trim.min(1).max(80),
    description: trim.max(300).optional().nullable(),
    kind: z.nativeEnum(PipelineKind).default(PipelineKind.ALTRO),
    stages: z.array(pipelineStageInput).max(40).optional(),
});
/** PATCH parziale: la schermata salva un campo alla volta, al cambio. */
export const updatePipelineInput = z.object({
    name: trim.min(1).max(80).optional(),
    description: trim.max(300).optional().nullable(),
    kind: z.nativeEnum(PipelineKind).optional(),
    active: z.boolean().optional(),
});
export const updatePipelineStageInput = z.object({
    name: trim.min(1).max(80).optional(),
    probability: z.coerce.number().int().min(0).max(100).optional(),
    outcome: z.nativeEnum(StageOutcome).optional(),
});
/** Riordino: un posto per volta, su o giù. Niente numeri d'ordine scritti a mano. */
export const moveOrderInput = z.object({ direction: z.enum(['su', 'giu']) });
export const stageIdParam = z.object({ stageId: z.string().min(1).max(40) });
/**
 * Cosa cambia davvero scegliendo il tipo di processo: il codice lo guarda per
 * decidere quali campi hanno senso. Serve alla schermata per spiegarlo a parole.
 */
export const PIPELINE_KIND_LABEL = {
    VOUCHER: 'Bando col contributo calcolato',
    RINNOVO: 'Rinnovo di contratti in essere',
    ESTENSIONE: 'Lavoro in più su clienti nostri',
    NUOVO: 'Nuovi clienti',
    ALTRO: 'Processo normale',
};
export const PIPELINE_KIND_HINT = {
    VOUCHER: 'Accende i campi del bando sulle trattative (configurazione da 20.000 o 40.000, requisiti, contributo) e calcola da sé quanto vale la pratica. Usalo solo per il Voucher Cloud e Cybersecurity.',
    RINNOVO: 'Le trattative si possono collegare al contratto che sta per scadere, così non si contano due volte gli stessi soldi.',
    ESTENSIONE: 'Importo libero. Pensato per il lavoro aggiuntivo su chi è già cliente.',
    NUOVO: 'Importo libero. Pensato per chi cliente non è ancora.',
    ALTRO: 'Importo libero, nessun campo in più. È la scelta giusta quasi sempre.',
};
//# sourceMappingURL=pipeline.js.map